import React from 'react';
import { Link } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import { BsSun, BsMoon } from 'react-icons/bs';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface Breadcrumb {
  label: string;
  path: string;
  isLast: boolean;
}

interface TopNavProps {
  breadcrumbs: Breadcrumb[];
  onMenuClick: () => void;
}

const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';

const TopNav: React.FC<TopNavProps> = ({ breadcrumbs, onMenuClick }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <header className={`border-b shadow-sm z-10 ${
      isDev
        ? 'bg-amber-50 dark:bg-gradient-to-r dark:from-amber-900/40 dark:to-dark-600 border-amber-200 dark:border-amber-500/30'
        : 'bg-white dark:bg-gradient-to-r dark:from-dark-500 dark:to-dark-600 border-gray-200 dark:border-dark-400/30 dark:shadow-lg dark:shadow-dark-900/50'
    }`}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Section: Menu Button & Breadcrumbs */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500"
            >
              <FiMenu className="h-6 w-6" />
            </button>

            {/* Dev Badge */}
            {isDev && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-400 dark:bg-amber-500 text-amber-900 dark:text-amber-950 rounded">
                DEV
              </span>
            )}

            {/* Breadcrumbs */}
            <nav className="hidden md:flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && (
                    <span className="text-gray-400 dark:text-gray-600 mx-1">/</span>
                  )}
                  {crumb.isLast ? (
                    <span className="text-gray-900 dark:text-white font-medium">{crumb.label}</span>
                  ) : (
                    <Link
                      to={crumb.path}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-primary-400 transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Right Section: Search, Notifications, Theme Toggle, User */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className="block w-64 pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-400/30 rounded-md dark:rounded-lg leading-5 bg-white dark:bg-dark-400/20 placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-md dark:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 transition-colors"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <BsMoon className="h-5 w-5" /> : <BsSun className="h-5 w-5" />}
            </button>

            {/* Notifications */}
            <button className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30 rounded-md dark:rounded-lg relative focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 dark:bg-danger-500 ring-2 ring-white dark:ring-dark-600"></span>
            </button>

            {/* User Menu */}
            <div className="flex items-center space-x-3 border-l border-gray-200 dark:border-dark-400/30 pl-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</p>
              </div>
              <img
                className="h-10 w-10 rounded-full ring-2 ring-gray-200 dark:ring-primary-500/50"
                src={user?.avatar || `https://ui-avatars.com/api/?name=Admin+User&background=${theme === 'dark' ? '2196F3' : '3b82f6'}&color=fff`}
                alt={`${user?.firstName} ${user?.lastName}`}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopNav;
